/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Tooltip, message } from "antd";
import { all_routes } from "../../../router/all_routes";
import {
  DisplayMode,
  EmployeeCardItem,
  OrgUnitPositionItem,
  ReportingLine,
  fetchManagerSelfServiceData,
  getAvatarDetails,
  getLoggedInEmpId,
  padEmpId,
} from "./managerSelfService.service";
import "./managerSelfService.scss";

interface HierarchyStep {
  empId: string;
  name: string;
}

const ManagerSelfService: React.FC = () => {
  const initialEmpId = useMemo(() => getLoggedInEmpId(), []);

  const [displayMode, setDisplayMode] = useState<DisplayMode>("EMPLOYEES");
  const [empId, setEmpId] = useState<string>(initialEmpId);
  const [inputEmpId, setInputEmpId] = useState<string>(initialEmpId);
  const [query, setQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");

  // Hierarchy drill-down navigation history
  const [drillHistory, setDrillHistory] = useState<HierarchyStep[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportingLine, setReportingLine] = useState<ReportingLine | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [employees, setEmployees] = useState<EmployeeCardItem[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitPositionItem[]>([]);
  // Selected item for Modal Detail View
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeCardItem | null>(null);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnitPositionItem | null>(null);

  // Debounce search query input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 400);
    return () => clearTimeout(handler);
  }, [query]);

  // Fetch data on parameters change
  const loadData = useCallback(
    async (mode: DisplayMode, targetEmpId: string, search: string) => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchManagerSelfServiceData({
          display: mode,
          empId: targetEmpId,
          query: search,
        });

        setReportingLine(result.reportingLine);
        setTotalRecords(result.totalCount);
        setEmployees(result.employees);
        setOrgUnits(result.orgUnits);
      } catch (err: any) {
        const msg = err?.message || "Failed to load manager self-service records.";
        setError(msg);
        setReportingLine(null);
        setTotalRecords(0);
        setEmployees([]);
        setOrgUnits([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadData(displayMode, empId, debouncedQuery);
  }, [displayMode, empId, debouncedQuery, loadData]);

  // Handle Manager Emp ID submit from input
  const handleApplyEmpId = (e: React.FormEvent) => {
    e.preventDefault();
    const padded = padEmpId(inputEmpId);
    setEmpId(padded);
    setInputEmpId(padded);
    setDrillHistory([]);
  };

  const handleResetEmpId = () => {
    const defaultId = getLoggedInEmpId();
    setInputEmpId(defaultId);
    setEmpId(defaultId);
    setDrillHistory([]);
  };

  // Drill down into any card employee's MSS hierarchy
  const handleDrillDown = (targetEmpId: string, employeeName?: string) => {
    if (!targetEmpId) return;
    const padded = padEmpId(targetEmpId);
    if (padded === empId) {
      message.info(`Already viewing MSS for ${employeeName || padded}`);
      return;
    }

    setDrillHistory((prev) => [
      ...prev,
      { empId: padded, name: employeeName || `Employee ${padded}` },
    ]);
    setEmpId(padded);
    setInputEmpId(padded);
    setQuery("");
    message.success(`Loading Manager Self-Service hierarchy for ${employeeName || padded}`);
  };

  // Navigate back to the initial logged-in user / root
  const handleBackToRoot = () => {
    setDrillHistory([]);
    setEmpId(initialEmpId);
    setInputEmpId(initialEmpId);
    setQuery("");
  };

  // Navigate back up one level in hierarchy
  const handleGoBackOneLevel = () => {
    if (drillHistory.length <= 1) {
      handleBackToRoot();
      return;
    }
    const newHistory = drillHistory.slice(0, -1);
    const prevStep = newHistory[newHistory.length - 1];
    setDrillHistory(newHistory);
    setEmpId(prevStep.empId);
    setInputEmpId(prevStep.empId);
    setQuery("");
  };

  // Navigate directly to a specific breadcrumb index
  const handleNavigateHistory = (index: number) => {
    const targetStep = drillHistory[index];
    const newHistory = drillHistory.slice(0, index + 1);
    setDrillHistory(newHistory);
    setEmpId(targetStep.empId);
    setInputEmpId(targetStep.empId);
    setQuery("");
  };

  const handleCopy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard!`);
  };

  // Filtered lists for searching
  const filteredEmployees = useMemo(() => {
    if (!debouncedQuery) return employees;
    const lower = debouncedQuery.toLowerCase();
    return employees.filter(
      (emp) =>
        String(emp.fullName || "").toLowerCase().includes(lower) ||
        String(emp.empId || "").toLowerCase().includes(lower) ||
        String(emp.designation || "").toLowerCase().includes(lower) ||
        String(emp.department || "").toLowerCase().includes(lower) ||
        String(emp.email || "").toLowerCase().includes(lower) ||
        String(emp.phone || "").includes(lower)
    );
  }, [employees, debouncedQuery]);

  const filteredOrgUnits = useMemo(() => {
    if (!debouncedQuery) return orgUnits;
    const lower = debouncedQuery.toLowerCase();
    return orgUnits.filter(
      (ou) =>
        String(ou.orgUnitName || "").toLowerCase().includes(lower) ||
        String(ou.positionTitle || "").toLowerCase().includes(lower) ||
        String(ou.holderName || "").toLowerCase().includes(lower) ||
        String(ou.holderEmpId || "").includes(lower) ||
        String(ou.orgUnitId || "").toLowerCase().includes(lower)
    );
  }, [orgUnits, debouncedQuery]);

  return (
    <div className="page-wrapper mss-container">
      <div className="content">
        {/* Page Header & Breadcrumb */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Manager Self-Service Module</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Other Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Manager Self-Service Module
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Header Overview Card */}
        <div className="mss-header-card">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <div className="mss-header-title">
                <i className="ti ti-users-group"></i>
                Manager Self-Service Overview
              </div>
              <p className="mss-header-subtitle">
                {reportingLine?.name
                  ? `${reportingLine.name}${reportingLine.designation ? ` · ${reportingLine.designation}` : ""}`
                  : "Team reporting for the selected manager"}
                {" · "}
                <span className="fw-bold text-dark font-monospace">{reportingLine?.empId || empId}</span>
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => loadData(displayMode, empId, debouncedQuery)}
                disabled={loading}
              >
                <i className={`ti ti-refresh ${loading ? "fa-spin" : ""}`}></i>
                Refresh
              </button>
            </div>
          </div>

          {/* Stat Badges (Dynamically matches the selected dropdown mode) */}
          <div className="mss-stats-row">
            {displayMode === "EMPLOYEES" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>All Subordinates</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredEmployees.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Active Status</span>
                  <span className="mss-stat-count bg-success">
                    {filteredEmployees.filter((e) => String(e.status || "").toLowerCase() === "active").length || filteredEmployees.length}
                  </span>
                </div>
              </>
            )}

            {displayMode === "DIRECT_REPORTING" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>Direct Reports</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredEmployees.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Active Status</span>
                  <span className="mss-stat-count bg-success">
                    {filteredEmployees.filter((e) => String(e.status || "").toLowerCase() === "active").length || filteredEmployees.length}
                  </span>
                </div>
              </>
            )}

            {displayMode === "ORG_UNITS_AND_POSITIONS" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>Org Units & Positions</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredOrgUnits.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Occupied Positions</span>
                  <span className="mss-stat-count bg-primary">
                    {filteredOrgUnits.filter((ou) => String(ou.status || "").toLowerCase() === "occupied").length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {!loading && reportingLine && (
          <div className="mss-reporting-line">
            <div className="mss-reporting-line-label">Reporting Line</div>
            <div className="mss-reporting-line-body">
              <div
                className="mss-avatar"
                style={{
                  backgroundColor: getAvatarDetails(reportingLine.name, reportingLine.empId).bg,
                  color: getAvatarDetails(reportingLine.name, reportingLine.empId).text,
                }}
              >
                {getAvatarDetails(reportingLine.name, reportingLine.empId).initial}
              </div>
              <div className="mss-reporting-line-meta">
                <div className="mss-reporting-line-name">{reportingLine.name}</div>
                <div className="mss-reporting-line-sub">
                  <span className="font-monospace">{reportingLine.empId}</span>
                  {reportingLine.designation ? <span>{reportingLine.designation}</span> : null}
                </div>
                {(reportingLine.orgUnitName || reportingLine.orgUnitID) && (
                  <div className="mss-reporting-line-org">
                    <i className="ti ti-building"></i>
                    <span>{reportingLine.orgUnitName || "Org Unit"}</span>
                    {reportingLine.orgUnitID ? (
                      <span className="font-monospace">{reportingLine.orgUnitID}</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hierarchy Trail Bar (Visible when drilled down into a subordinate manager) */}
        {drillHistory.length > 0 && (
          <div className="alert alert-info py-2 px-3 d-flex align-items-center justify-content-between mb-3 rounded-3 border-0 bg-primary-subtle text-primary shadow-sm">
            <div className="d-flex align-items-center gap-2 flex-wrap small">
              <i className="ti ti-sitemap fs-5"></i>
              <span className="fw-semibold text-dark">Hierarchy Trail:</span>
              <button
                className="btn btn-link btn-sm p-0 text-decoration-none fw-bold text-primary"
                onClick={handleBackToRoot}
                title="Return to initial manager team"
              >
                Root ({initialEmpId})
              </button>
              {drillHistory.map((step, idx) => (
                <React.Fragment key={step.empId + idx}>
                  <i className="ti ti-chevron-right fs-6 text-muted"></i>
                  {idx === drillHistory.length - 1 ? (
                    <span className="fw-bold text-dark font-monospace bg-white px-2 py-1 rounded border">
                      {step.name} ({step.empId})
                    </span>
                  ) : (
                    <button
                      className="btn btn-link btn-sm p-0 text-decoration-none text-primary"
                      onClick={() => handleNavigateHistory(idx)}
                    >
                      {step.name} ({step.empId})
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-primary py-1 px-2 d-inline-flex align-items-center gap-1"
                onClick={handleGoBackOneLevel}
              >
                <i className="ti ti-arrow-left"></i>
                Back 1 Level
              </button>
              <button
                className="btn btn-sm btn-primary py-1 px-2 d-inline-flex align-items-center gap-1"
                onClick={handleBackToRoot}
              >
                <i className="ti ti-home"></i>
                My Root Team
              </button>
            </div>
          </div>
        )}

        {/* Filter & Search Bar */}
        <div className="mss-controls-card">
          <div className="row g-3 align-items-center">
            {/* Display Mode Dropdown */}
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative">
                <select
                  className="form-select mss-dropdown-select"
                  value={displayMode}
                  onChange={(e) => setDisplayMode(e.target.value as DisplayMode)}
                >
                  <option value="EMPLOYEES">👥 Employees</option>
                  <option value="DIRECT_REPORTING">👤 Direct Reports</option>
                  <option value="ORG_UNITS_AND_POSITIONS">🏢 Org Units & Positions</option>
                </select>
              </div>
            </div>

            {/* Search Input */}
            <div className="col-12 col-sm-6 col-lg-5">
              <div className="mss-search-input-group">
                <i className="ti ti-search search-icon"></i>
                <input
                  type="text"
                  className="form-control mss-search-input"
                  placeholder="Search by name, ID, role..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button className="clear-btn" onClick={() => setQuery("")}>
                    <i className="ti ti-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Manager ID Selector */}
            <div className="col-12 col-lg-3">
              <form onSubmit={handleApplyEmpId} className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control mss-empid-input font-monospace"
                  placeholder="Manager ID"
                  value={inputEmpId}
                  onChange={(e) => setInputEmpId(e.target.value)}
                  maxLength={10}
                />
                <button type="submit" className="btn btn-primary px-3" title="Apply Manager ID">
                  Apply
                </button>
                {inputEmpId !== initialEmpId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-2"
                    onClick={handleResetEmpId}
                    title="Reset to default ID"
                  >
                    <i className="ti ti-rotate-clockwise"></i>
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && !loading && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between mb-4" role="alert">
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-alert-circle fs-5"></i>
              <div>
                <strong>Error:</strong> {error}
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => loadData(displayMode, empId, debouncedQuery)}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State: Skeleton Cards */}
        {loading && (
          <div className="mss-cards-grid">
            {[1, 2, 3, 4, 5, 6].map((k) => (
              <div key={k} className="mss-skeleton-card">
                <div className="d-flex align-items-center gap-3">
                  <div className="skeleton-avatar"></div>
                  <div className="flex-1 d-flex flex-column gap-2" style={{ flex: 1 }}>
                    <div className="skeleton-line title"></div>
                    <div className="skeleton-line sub"></div>
                  </div>
                </div>
                <div className="skeleton-line full"></div>
                <div className="skeleton-line short"></div>
                <div className="skeleton-line full"></div>
              </div>
            ))}
          </div>
        )}

        {/* Content Display: Cards Grid */}
        {!loading && !error && (
          <>
            {displayMode === "ORG_UNITS_AND_POSITIONS" && filteredOrgUnits.length > 0 ? (
              <div className="mss-cards-grid">
                {filteredOrgUnits.map((ou, index) => (
                  <div key={ou.id || index} className="mss-emp-card mss-org-card">
                    <div className="mss-org-header">
                      <span className="mss-org-unit-badge">Unit: {ou.orgUnitId}</span>
                      <h4 className="mss-org-unit-name">{ou.orgUnitName}</h4>
                    </div>

                    <div className="mss-pos-section flex-1">
                      <div className="mss-pos-title">
                        <i className="ti ti-briefcase"></i>
                        <span>{ou.positionTitle}</span>
                      </div>
                      <div className="text-muted small mb-2">Position Code: {ou.positionId}</div>

                      <div className="mss-holder-box">
                        <div
                          className="mss-avatar"
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 8,
                            background: ou.holderEmpId ? "#eff6ff" : "#f1f5f9",
                            color: ou.holderEmpId ? "#2563eb" : "#64748b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {ou.holderName ? ou.holderName.slice(0, 2).toUpperCase() : "VA"}
                        </div>
                        <div className="flex-1">
                          <div className="holder-name">{ou.holderName}</div>
                          {ou.holderEmpId && <div className="holder-id">Emp ID: {ou.holderEmpId}</div>}
                        </div>
                      </div>

                      {ou.reportsToPosition && (
                        <div className="small text-muted mt-2">
                          <i className="ti ti-arrow-up-right me-1"></i>
                          Reports to: {ou.reportsToPosition}
                        </div>
                      )}
                    </div>

                    {/* Internal Hierarchies Section (if present) */}
                    {ou.internalHierarchies && ou.internalHierarchies.length > 0 && (
                      <div className="mss-internal-hierarchies-box">
                        <div className="mss-hier-header">
                          <div className="hier-title">
                            <i className="ti ti-hierarchy-2"></i>
                            <span>Internal Hierarchies</span>
                          </div>
                          <span className="hier-count-pill">
                            {ou.internalHierarchies.length} {ou.internalHierarchies.length === 1 ? "position" : "positions"}
                          </span>
                        </div>
                        <div className="mss-hier-list">
                          {ou.internalHierarchies.map((hier, hIdx) => (
                            <div key={hier.id || hIdx} className="mss-hier-node">
                              <div className="hier-pos-title d-flex align-items-center justify-content-between">
                                <span>{hier.positionTitle || hier.designation || "Position"}</span>
                                <span
                                  className={`badge ${
                                    String(hier.status || "").toLowerCase() === "occupied"
                                      ? "bg-primary-subtle text-primary"
                                      : "bg-danger-subtle text-danger"
                                  }`}
                                  style={{ fontSize: "10px" }}
                                >
                                  {hier.status || (hier.holderEmpId ? "Occupied" : "Vacant")}
                                </span>
                              </div>
                              <div className="hier-holder-row mt-1">
                                <div className="d-flex align-items-center gap-1">
                                  <i className="ti ti-user small"></i>
                                  <span className="hier-holder-name">{hier.holderName}</span>
                                  {hier.holderEmpId && (
                                    <span className="text-muted font-monospace small">({hier.holderEmpId})</span>
                                  )}
                                </div>
                                {hier.holderEmpId && (
                                  <button
                                    className="btn btn-sm btn-link p-0 text-danger text-decoration-none"
                                    style={{ fontSize: "11px", fontWeight: 600 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDrillDown(hier.holderEmpId!, hier.holderName);
                                    }}
                                  >
                                    <i className="ti ti-sitemap me-1"></i>MSS
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mss-card-footer">
                      <span
                        className={`mss-status-badge ${String(ou.status || "").toLowerCase() === "occupied"
                            ? "badge-occupied"
                            : "badge-vacant"
                          }`}
                      >
                        <i
                          className={`ti ${String(ou.status || "").toLowerCase() === "occupied"
                              ? "ti-check"
                              : "ti-alert-circle"
                            }`}
                        ></i>
                        {ou.status}
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        {ou.holderEmpId && (
                          <Tooltip title={`Explore hierarchy & team for ${ou.holderName}`}>
                            <button
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                              style={{ fontSize: "11.5px", padding: "4px 8px", borderRadius: "6px" }}
                              onClick={() => handleDrillDown(ou.holderEmpId, ou.holderName)}
                            >
                              <i className="ti ti-sitemap"></i>
                              <span>View MSS</span>
                            </button>
                          </Tooltip>
                        )}
                        <button
                          className="mss-view-btn"
                          onClick={() => setSelectedOrgUnit(ou)}
                        >
                          <i className="ti ti-eye"></i>
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredEmployees.length > 0 ? (
              <div className="mss-cards-grid">
                {filteredEmployees.map((emp, index) => {
                  const avatar = getAvatarDetails(emp.fullName, emp.empId);
                  const isDirect = displayMode === "DIRECT_REPORTING" || emp.reportingManager?.includes(empId);

                  return (
                    <div key={emp.id || index} className="mss-emp-card">
                      <div className="mss-card-accent-bar"></div>

                      {/* Card Header */}
                      <div className="mss-card-header">
                        <div className="mss-avatar-wrapper">
                          {emp.image ? (
                            <img
                              src={`data:image/jpeg;base64,${emp.image}`}
                              alt={emp.fullName}
                              className="mss-avatar"
                            />
                          ) : (
                            <div
                              className="mss-avatar"
                              style={{ backgroundColor: avatar.bg, color: avatar.text }}
                            >
                              {avatar.initial}
                            </div>
                          )}
                          <span
                            className={`mss-status-dot ${String(emp.status || "").toLowerCase() === "active"
                                ? ""
                                : String(emp.status || "").toLowerCase().includes("leave")
                                  ? "status-leave"
                                  : "status-inactive"
                              }`}
                            title={`Status: ${emp.status}`}
                          ></span>
                        </div>

                        <div className="mss-emp-info">
                          <h4 className="mss-emp-name" title={emp.fullName}>
                            {emp.fullName}
                          </h4>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Tooltip title="Click to copy Employee ID">
                              <span
                                className="mss-emp-id-badge"
                                onClick={() => handleCopy(emp.empId, "Employee ID")}
                              >
                                <i className="ti ti-copy"></i>
                                {emp.empId}
                              </span>
                            </Tooltip>
                            {isDirect && (
                              <span className="badge bg-danger-subtle text-danger font-monospace" style={{ fontSize: '10px' }}>
                                Direct Report
                              </span>
                            )}
                          </div>

                          <div className="mss-emp-designation" title={emp.designation}>
                            {emp.designation}
                          </div>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="mss-card-body">
                        {emp.department && (
                          <div className="mss-detail-row">
                            <i className="ti ti-building detail-icon"></i>
                            <div className="detail-text fw-medium">{emp.department}</div>
                          </div>
                        )}

                        {(emp.location || emp.wing) && (
                          <div className="mss-detail-row">
                            <i className="ti ti-map-pin detail-icon"></i>
                            <div className="detail-text">
                              {emp.location || emp.wing}
                              {emp.location && emp.wing ? ` (${emp.wing})` : ""}
                            </div>
                          </div>
                        )}

                        {emp.email && (
                          <div className="mss-detail-row">
                            <i className="ti ti-mail detail-icon"></i>
                            <div className="detail-text">
                              <a href={`mailto:${emp.email}`}>{emp.email}</a>
                            </div>
                          </div>
                        )}

                        {emp.phone && (
                          <div className="mss-detail-row">
                            <i className="ti ti-phone detail-icon"></i>
                            <div className="detail-text">
                              <a href={`tel:${emp.phone}`}>{emp.phone}</a>
                            </div>
                          </div>
                        )}

                        {emp.reportingManager && (
                          <div className="mss-detail-row">
                            <i className="ti ti-user-check detail-icon"></i>
                            <div className="detail-text text-muted">
                              Reports To: <span className="text-dark">{emp.reportingManager}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="mss-card-footer">
                        <span className="mss-status-badge badge-active">
                          <i className="ti ti-circle-filled" style={{ fontSize: "8px" }}></i>
                          {emp.status || "Active"}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <Tooltip title={`View Manager Self-Service hierarchy for ${emp.fullName}`}>
                            <button
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                              style={{ fontSize: "11.5px", padding: "4px 8px", borderRadius: "6px" }}
                              onClick={() => handleDrillDown(emp.empId, emp.fullName)}
                            >
                              <i className="ti ti-sitemap"></i>
                              <span>View MSS</span>
                            </button>
                          </Tooltip>
                          <button
                            className="mss-view-btn"
                            onClick={() => setSelectedEmployee(emp)}
                          >
                            <i className="ti ti-user"></i>
                            Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Empty State */
              <div className="mss-empty-card">
                <i className="ti ti-users-minus mss-empty-icon"></i>
                <h4>No Team Records</h4>
                <p>
                  {reportingLine?.name ? (
                    <>
                      <strong>{reportingLine.name}</strong> ({reportingLine.empId}) has no team records
                      under display mode <strong>{displayMode}</strong>
                      {debouncedQuery ? ` matching "${debouncedQuery}"` : ""}.
                    </>
                  ) : (
                    <>
                      No team records found for Manager ID <strong>{empId}</strong> under display mode{" "}
                      <strong>{displayMode}</strong>
                      {debouncedQuery ? ` matching "${debouncedQuery}"` : ""}.
                    </>
                  )}
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setQuery("");
                      loadData(displayMode, empId, "");
                    }}
                  >
                    Clear Filters & Reload
                  </button>
                  {drillHistory.length > 0 && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleBackToRoot}
                    >
                      <i className="ti ti-home me-1"></i>
                      Return to Root Manager
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal: Employee Profile Details */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-id-badge text-danger fs-4"></i>
              <span>Employee Profile Details</span>
            </div>
          }
          open={!!selectedEmployee}
          onCancel={() => setSelectedEmployee(null)}
          footer={[
            selectedEmployee && (
              <button
                key="drill"
                className="btn btn-danger btn-sm me-auto d-inline-flex align-items-center gap-1"
                onClick={() => {
                  const target = selectedEmployee;
                  setSelectedEmployee(null);
                  handleDrillDown(target.empId, target.fullName);
                }}
              >
                <i className="ti ti-sitemap"></i>
                Explore this Employee's MSS
              </button>
            ),
            <button
              key="close"
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedEmployee(null)}
            >
              Close
            </button>,
          ]}
          width={650}
        >
          {selectedEmployee && (
            <div>
              {/* Header inside modal */}
              <div className="d-flex align-items-center gap-3 p-3 bg-light rounded mb-3">
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 12,
                    background: getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).bg,
                    color: getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).text,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).initial}
                </div>
                <div className="flex-1">
                  <h5 className="mb-0 fw-bold">{selectedEmployee.fullName}</h5>
                  <div className="text-muted small">
                    Employee ID: <span className="fw-semibold text-dark font-monospace">{selectedEmployee.empId}</span> | {selectedEmployee.designation}
                  </div>
                </div>
              </div>

              {/* Detail fields */}
              <div className="table-responsive">
                <table className="table table-bordered table-sm mb-0">
                  <tbody>
                    <tr>
                      <th className="bg-light" style={{ width: "35%" }}>Full Name</th>
                      <td>{selectedEmployee.fullName}</td>
                    </tr>
                    <tr>
                      <th className="bg-light">Employee ID</th>
                      <td>
                        <span className="font-monospace fw-bold">{selectedEmployee.empId}</span>
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-light">Designation</th>
                      <td>{selectedEmployee.designation}</td>
                    </tr>
                    <tr>
                      <th className="bg-light">Department</th>
                      <td>{selectedEmployee.department}</td>
                    </tr>
                    {selectedEmployee.wing && (
                      <tr>
                        <th className="bg-light">Wing / Cadre</th>
                        <td>{selectedEmployee.wing}</td>
                      </tr>
                    )}
                    {selectedEmployee.grade && (
                      <tr>
                        <th className="bg-light">Grade / Pay Scale</th>
                        <td>{selectedEmployee.grade}</td>
                      </tr>
                    )}
                    {selectedEmployee.location && (
                      <tr>
                        <th className="bg-light">Office Location</th>
                        <td>{selectedEmployee.location}</td>
                      </tr>
                    )}
                    {selectedEmployee.email && (
                      <tr>
                        <th className="bg-light">Official Email</th>
                        <td>
                          <a href={`mailto:${selectedEmployee.email}`}>{selectedEmployee.email}</a>
                        </td>
                      </tr>
                    )}
                    {selectedEmployee.phone && (
                      <tr>
                        <th className="bg-light">Contact Number</th>
                        <td>
                          <a href={`tel:${selectedEmployee.phone}`}>{selectedEmployee.phone}</a>
                        </td>
                      </tr>
                    )}
                    {selectedEmployee.reportingManager && (
                      <tr>
                        <th className="bg-light">Reporting Manager</th>
                        <td>{selectedEmployee.reportingManager}</td>
                      </tr>
                    )}
                    {selectedEmployee.dateOfJoining && (
                      <tr>
                        <th className="bg-light">Date of Joining</th>
                        <td>{selectedEmployee.dateOfJoining}</td>
                      </tr>
                    )}
                    <tr>
                      <th className="bg-light">Employment Status</th>
                      <td>
                        <span className="badge bg-success">{selectedEmployee.status || "Active"}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal>

        {/* Modal: Org Unit Details */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-hierarchy-2 text-primary fs-4"></i>
              <span>Organizational Unit & Position Details</span>
            </div>
          }
          open={!!selectedOrgUnit}
          onCancel={() => setSelectedOrgUnit(null)}
          footer={[
            selectedOrgUnit?.holderEmpId && (
              <button
                key="drill"
                className="btn btn-danger btn-sm me-auto d-inline-flex align-items-center gap-1"
                onClick={() => {
                  const target = selectedOrgUnit;
                  setSelectedOrgUnit(null);
                  handleDrillDown(target.holderEmpId, target.holderName);
                }}
              >
                <i className="ti ti-sitemap"></i>
                Explore Holder's MSS
              </button>
            ),
            <button
              key="close"
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedOrgUnit(null)}
            >
              Close
            </button>,
          ]}
          width={600}
        >
          {selectedOrgUnit && (
            <div className="table-responsive">
              <table className="table table-bordered table-sm mb-0">
                <tbody>
                  <tr>
                    <th className="bg-light" style={{ width: "35%" }}>Org Unit ID</th>
                    <td className="font-monospace">{selectedOrgUnit.orgUnitId}</td>
                  </tr>
                  <tr>
                    <th className="bg-light">Org Unit Name</th>
                    <td className="fw-semibold">{selectedOrgUnit.orgUnitName}</td>
                  </tr>
                  <tr>
                    <th className="bg-light">Position ID</th>
                    <td className="font-monospace">{selectedOrgUnit.positionId}</td>
                  </tr>
                  <tr>
                    <th className="bg-light">Position Title</th>
                    <td>{selectedOrgUnit.positionTitle}</td>
                  </tr>
                  <tr>
                    <th className="bg-light">Holder Name</th>
                    <td className="fw-bold">{selectedOrgUnit.holderName}</td>
                  </tr>
                  {selectedOrgUnit.holderEmpId && (
                    <tr>
                      <th className="bg-light">Holder Emp ID</th>
                      <td className="font-monospace">{selectedOrgUnit.holderEmpId}</td>
                    </tr>
                  )}
                  {selectedOrgUnit.reportsToPosition && (
                    <tr>
                      <th className="bg-light">Reports to Position</th>
                      <td>{selectedOrgUnit.reportsToPosition}</td>
                    </tr>
                  )}
                  <tr>
                    <th className="bg-light">Staffing Status</th>
                    <td>
                      <span
                        className={`badge ${String(selectedOrgUnit.status || "").toLowerCase() === "occupied"
                            ? "bg-primary"
                            : "bg-danger"
                          }`}
                      >
                        {selectedOrgUnit.status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              {selectedOrgUnit.internalHierarchies && selectedOrgUnit.internalHierarchies.length > 0 && (
                <div className="mt-3">
                  <h6 className="fw-bold d-flex align-items-center gap-2 mb-2 text-dark">
                    <i className="ti ti-hierarchy-2 text-primary"></i>
                    <span>Internal Hierarchies ({selectedOrgUnit.internalHierarchies.length})</span>
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Position</th>
                          <th>Holder</th>
                          <th>Emp ID</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrgUnit.internalHierarchies.map((hier, hIdx) => (
                          <tr key={hier.id || hIdx}>
                            <td className="fw-semibold">{hier.positionTitle}</td>
                            <td>{hier.holderName}</td>
                            <td className="font-monospace">{hier.holderEmpId || "-"}</td>
                            <td>
                              <span
                                className={`badge ${
                                  String(hier.status || "").toLowerCase() === "occupied"
                                    ? "bg-primary-subtle text-primary"
                                    : "bg-danger-subtle text-danger"
                                }`}
                                style={{ fontSize: "11px" }}
                              >
                                {hier.status || (hier.holderEmpId ? "Occupied" : "Vacant")}
                              </span>
                            </td>
                            <td>
                              {hier.holderEmpId ? (
                                <button
                                  className="btn btn-xs btn-outline-danger py-0 px-2"
                                  style={{ fontSize: "11px" }}
                                  onClick={() => {
                                    const empId = hier.holderEmpId!;
                                    const name = hier.holderName;
                                    setSelectedOrgUnit(null);
                                    handleDrillDown(empId, name);
                                  }}
                                >
                                  View MSS
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ManagerSelfService;
